import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Megaphone, Plus, Trash2, X } from 'lucide-react';
import { toast } from 'sonner';
import { ApiError, api } from '../lib/api';
import { formatDateTime } from '../lib/format';
import { Badge, Button, Card, CheckRow, EmptyState, Modal, TableSkeleton, TextAreaField, TextField, cx } from './ui';

export interface Announcement { id: string; title: string; body: string; level: 'info' | 'important'; startsAt: string; endsAt: string | null; fromPlatform: boolean; createdAt: string; readCount?: number }

/** Banner at the top of the app with the newest announcement this person hasn't dismissed. */
export function AnnouncementBanner() {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['announcements', 'active'], queryFn: () => api.get<Announcement[]>('/announcements/active'), refetchInterval: 5 * 60_000, retry: false });
  const [expanded, setExpanded] = useState(false);
  const a = q.data?.[0];
  if (!a) return null;
  const dismiss = async () => { await api.post(`/announcements/${a.id}/read`); setExpanded(false); await qc.invalidateQueries({ queryKey: ['announcements', 'active'] }); };
  return (
    <div className={cx('border-b px-4 py-2 text-sm', a.level === 'important' ? 'border-red-200 bg-red-50 text-red-900 dark:border-red-900 dark:bg-red-950 dark:text-red-100' : 'border-brand-100 bg-brand-50 text-brand-900 dark:border-slate-800 dark:bg-slate-900 dark:text-slate-100')}>
      <div className="mx-auto flex max-w-6xl items-start gap-3">
        <Megaphone className="mt-0.5 size-4 shrink-0" />
        <div className="min-w-0 flex-1">
          <button className="text-left" onClick={() => setExpanded((v) => !v)}>
            <strong>{a.title}</strong> {!expanded && <span className="opacity-80">— {a.body.slice(0, 90)}{a.body.length > 90 ? '…' : ''}</span>}
          </button>
          {expanded && <p className="mt-1 whitespace-pre-wrap">{a.body}</p>}
          <p className="text-xs opacity-70">{a.fromPlatform ? 'From AI CRM' : 'From your company'}</p>
        </div>
        <button onClick={dismiss} className="rounded p-1 hover:bg-black/5" aria-label="Dismiss announcement"><X className="size-4" /></button>
      </div>
    </div>
  );
}

/** List + compose, used by company admins (to their team) and the super admin (to every company). */
export function AnnouncementManager({ path, audience }: { path: string; audience: string }) {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['announcements', path], queryFn: () => api.get<Announcement[]>(path) });
  const [composing, setComposing] = useState(false);
  const remove = async (a: Announcement) => { await api.del(`${path}/${a.id}`); await qc.invalidateQueries({ queryKey: ['announcements'] }); };
  return (
    <>
      <div className="mb-4 flex justify-end"><Button icon={<Plus className="size-4" />} onClick={() => setComposing(true)}>New announcement</Button></div>
      <Card>
        {!q.data ? <TableSkeleton /> : q.data.length === 0 ? (
          <EmptyState icon={<Megaphone className="size-6" />} title="No announcements yet" description={`Announcements show as a banner at the top of the app for ${audience}.`} />
        ) : (
          <ul className="divide-y divide-slate-100 dark:divide-slate-800">
            {q.data.map((a) => (
              <li key={a.id} className="flex items-start gap-3 px-4 py-3">
                <div className="min-w-0 flex-1">
                  <p className="flex flex-wrap items-center gap-2 font-medium">{a.title}{a.level === 'important' && <Badge tone="red">Important</Badge>}
                    {a.endsAt && new Date(a.endsAt) < new Date() && <Badge>Ended</Badge>}</p>
                  <p className="text-sm whitespace-pre-wrap text-slate-600 dark:text-slate-400">{a.body}</p>
                  <p className="text-xs text-slate-500">{formatDateTime(a.createdAt)}{a.endsAt ? ` · until ${formatDateTime(a.endsAt)}` : ''}{a.readCount !== undefined ? ` · dismissed by ${a.readCount}` : ''}</p>
                </div>
                <Button size="sm" variant="ghost" aria-label="Delete announcement" icon={<Trash2 className="size-4 text-red-600" />} onClick={() => remove(a)} />
              </li>
            ))}
          </ul>
        )}
      </Card>
      {composing && <Compose path={path} audience={audience} onClose={() => setComposing(false)} />}
    </>
  );
}

function Compose({ path, audience, onClose }: { path: string; audience: string; onClose: () => void }) {
  const qc = useQueryClient();
  const [f, setF] = useState({ title: '', body: '', important: false, endsAt: '' });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const send = async () => {
    setBusy(true); setErrors({});
    try {
      await api.post(path, { title: f.title, body: f.body, level: f.important ? 'important' : 'info', endsAt: f.endsAt ? new Date(f.endsAt).toISOString() : null });
      await qc.invalidateQueries({ queryKey: ['announcements'] }); toast.success('Announcement published.'); onClose();
    } catch (e) { if (e instanceof ApiError) setErrors(e.fields); toast.error(e instanceof Error ? e.message : 'Couldn’t publish.'); }
    finally { setBusy(false); }
  };
  return (
    <Modal open onClose={onClose} title="New announcement" footer={<><Button variant="secondary" onClick={onClose}>Cancel</Button><Button loading={busy} onClick={send}>Publish</Button></>}>
      <div className="space-y-4">
        <TextField label="Title" value={f.title} error={errors.title} onChange={(e) => setF({ ...f, title: e.target.value })} />
        <TextAreaField label="Message" rows={5} value={f.body} error={errors.body} onChange={(e) => setF({ ...f, body: e.target.value })} />
        <TextField label="Show until (optional)" type="datetime-local" value={f.endsAt} onChange={(e) => setF({ ...f, endsAt: e.target.value })} />
        <CheckRow label="Important" hint={`Shown in red${path.startsWith('/platform') ? '' : ' and sent as a notification to everyone'}.`} checked={f.important} onChange={(v) => setF({ ...f, important: v })} />
        <p className="text-sm text-slate-500">This will be shown to {audience}.</p>
      </div>
    </Modal>
  );
}
